import { useCallback, useEffect, useMemo, useRef, useState } from 'react'; import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocFromServer,
    getDocs,
    query,
    setDoc,
    updateDoc,
    where,
    limit,
    deleteField,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword } from 'firebase/auth';
import { initializeApp as initializeAppSecondary, getApp, deleteApp } from 'firebase/app';
import { getAuth as getAuthSecondary } from 'firebase/auth';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '../components/navbar';
import { db, firebaseConfig } from '../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

// --------------------
// Helpers
// --------------------
const CHAPTER_TABS = ['home', 'talks', 'users'];

const trim = (v) => (typeof v === 'string' ? v.trim() : v);

const formatIsoForDisplay = (iso) => {
    const s = String(iso || '').trim();
    if (!s) return '';
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(d);
    } catch (e) {
        return s;
    }
};
const nowIso = () => new Date().toISOString();

const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        if (v === undefined || v === null) continue;
        const s = String(v).trim();
        if (s) return s;
    }
    return "";
};

function getGlobalRoleFromUserDoc(data) {
    // Treat "corporate" as a global super-admin role (can manage every chapter)
    const corporateFields = [
        data?.userType,
        data?.accountType,
        data?.type,
        data?.globalRole,
        data?.userRole,
        data?.role,
        data?.permissionsRole,
        data?.permissions?.role,
    ];

    for (const v of corporateFields) {
        const s = String(v || '').toLowerCase().trim();
        if (s === 'corporate') return 'corporate';
        if (s === 'national') return 'national';
    }

    // Otherwise fall back to whatever role-like field exists
    return firstNonEmpty(
        data?.role,
        data?.userRole,
        data?.globalRole,
        data?.accountRole,
        data?.accountType,
        data?.type,
        data?.userType,
        data?.permissionsRole,
        data?.permissions?.role
    );
}

function normalizeRole(role) {
    const r = String(role || '').toLowerCase().trim();
    if (r === 'president') return 'president';
    if (r === 'leader') return 'leader';
    return 'member';
}

function isCorporateRole(role) {
    const r = String(role || '').toLowerCase().trim();
    // "corporate" is the only global full-admin role.
    return r === 'corporate';
}

function isNationalEditorRole(role) {
    const r = String(role || '').toLowerCase().trim();
    // National team editors (can edit chapter content, but not necessarily manage users)
    return r === 'national' || r === 'writer' || r === 'publisher' || r === 'deacon';
}

async function getGlobalRoleForAuthUser(db, authUser) {
    if (!authUser) return "";

    // 1) Most common schema: users/{uid}
    try {
        const direct = await getDoc(doc(db, "users", authUser.uid));
        if (direct.exists()) {
            return getGlobalRoleFromUserDoc(direct.data() || {});
        }
    } catch (e) {
        // ignore and try fallbacks
    }

    // 2) Fallback: query users by email (handles older schemas where docId != uid)
    const email = String(authUser.email || "").trim().toLowerCase();
    if (!email) return "";

    // Try common field names
    const fields = ["email", "Email", "userEmail"];
    for (const field of fields) {
        try {
            const qs = await getDocs(query(collection(db, "users"), where(field, "==", email), limit(1)));
            if (!qs.empty) {
                const data = qs.docs[0].data() || {};
                return getGlobalRoleFromUserDoc(data);
            }
        } catch (e) {
            // ignore and continue
        }
    }

    return "";
}

function getDisplayNameFromUserDoc(data) {
    return firstNonEmpty(
        data?.name,
        data?.displayName,
        data?.fullName,
        data?.firstName ? `${data.firstName} ${data.lastName || ''}`.trim() : '',
        data?.email
    );
}

async function getDisplayNameForAuthUser(db, authUser) {
    if (!authUser) return '';

    // 1) users/{uid}
    try {
        const direct = await getDoc(doc(db, 'users', authUser.uid));
        if (direct.exists()) return getDisplayNameFromUserDoc(direct.data() || {});
    } catch (e) {
        // ignore
    }

    // 2) query by email (docId != uid)
    const email = String(authUser.email || '').trim().toLowerCase();
    if (!email) return '';
    const fields = ['email', 'Email', 'userEmail'];
    for (const field of fields) {
        try {
            const qs = await getDocs(query(collection(db, 'users'), where(field, '==', email), limit(1)));
            if (!qs.empty) return getDisplayNameFromUserDoc(qs.docs[0].data() || {});
        } catch (e) {
            // ignore
        }
    }

    return '';
}

function canAddMembers(role, isCorporate = false) {
    if (isCorporate) return true;
    const r = normalizeRole(role);
    return r === "president" || r === "leader";
}

function canManageMembers(role, isCorporate = false) {
    if (isCorporate) return true;
    return normalizeRole(role) === "president";
}

const blankEvent = () => ({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    talkLink: '',
    talkNotesLink: '',
    talkRecordingLink: '',
    attendanceCount: '',
    attendanceNotes: '',
});

const MAX_DATE = new Date(8640000000000000);
const toDateObj = (evt) => {
    if (!evt || !evt.date) return MAX_DATE;
    const [y, m, d] = String(evt.date).split('-').map(Number);
    const [hh = 0, mm = 0] = String(evt.time || '00:00').split(':').map(Number);
    return new Date(y || 0, (m || 1) - 1, d || 1, hh || 0, mm || 0);
};

const sortEvents = (arr = []) =>
    [...arr].sort((a, b) => {
        const da = toDateObj(a).getTime();
        const dbv = toDateObj(b).getTime();
        if (da !== dbv) return da - dbv;
        return String(a.title || '').localeCompare(String(b.title || ''));
    });

function labelSchool(s) {
    return (
        s?.displayName ||
        s?.name ||
        s?.schoolName ||
        (typeof s?.id === 'string' ? s.id.replace(/[-_]/g, ' ').toUpperCase() : 'School')
    );
}

function fallbackSemesterName() {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const isSpring = m <= 5;
    const isSummer = m >= 6 && m <= 7;
    const term = isSpring ? 'Spring' : isSummer ? 'Summer' : 'Fall';
    return `${term} ${y}`;
}

function semesterIdFromName(name) {
    return String(name || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64);
}

function normalizeSemester(s) {
    const name = trim(s?.name) || fallbackSemesterName();
    return {
        id: trim(s?.id) || semesterIdFromName(name) || `sem_${Date.now()}`,
        name,
        // public window
        publicFrom: s?.publicFrom || '', // YYYY-MM-DD
        publicTo: s?.publicTo || '', // YYYY-MM-DD
        // (removed) start/end window; publicFrom/publicTo is the visibility window
        announcements: s?.announcements || '',
        events: Array.isArray(s?.events) ? sortEvents(s.events) : [],
    };
}

function coerceSemestersFromSchoolDoc(data) {
    // New model: data.semesters[]
    if (Array.isArray(data?.semesters) && data.semesters.length) {
        return data.semesters.map(normalizeSemester);
    }

    // Backward compat: data.events + data.announcements
    const legacyEvents = Array.isArray(data?.events) ? data.events : [];
    const legacyAnnouncements = data?.announcements || '';

    if (legacyEvents.length || legacyAnnouncements) {
        return [
            normalizeSemester({
                id: 'default',
                name: fallbackSemesterName(),
                announcements: legacyAnnouncements,
                events: legacyEvents,
            }),
        ];
    }

    // Default empty semester
    return [normalizeSemester({ id: 'default', name: fallbackSemesterName() })];
}

function coerceMembersFromSchoolDoc(data) {
    // Already an array
    if (Array.isArray(data?.members) && data.members.length) {
        return data.members
            .map((m, idx) => ({
                id: m.id || m.uid || m.email || `m_${idx}`,
                ...(m || {}),
            }))
            .filter((m) => m.email || m.uid);
    }

    // Map form: membersByEmail: { "a@b.com": "leader" }
    const byEmail = data?.membersByEmail || data?.memberRolesByEmail || data?.memberRoles;
    if (byEmail && typeof byEmail === 'object' && !Array.isArray(byEmail)) {
        return Object.entries(byEmail)
            .map(([email, role]) => ({ id: email, email, role }))
            .filter((m) => m.email);
    }

    // Common single fields
    const presidentEmail = data?.presidentEmail || data?.president || data?.chapterPresidentEmail;
    const leaderEmails = data?.leaderEmails || data?.leaders || data?.chapterLeaders;
    const memberEmails = data?.memberEmails || data?.chapterMembers;

    const out = [];
    if (presidentEmail) out.push({ id: String(presidentEmail), email: String(presidentEmail), role: 'president' });

    if (Array.isArray(leaderEmails)) {
        for (const e of leaderEmails) out.push({ id: String(e), email: String(e), role: 'leader' });
    }

    if (Array.isArray(memberEmails)) {
        for (const e of memberEmails) out.push({ id: String(e), email: String(e), role: 'member' });
    }

    // De-dupe by email
    const seen = new Set();
    return out.filter((m) => {
        const key = String(m.email || '').toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// --------------------
// Component
// --------------------
export default function ChapterDashboard() {
    const params = useParams();
    const shellRef = useRef(null);

    // Route: /manage/:slug (support several param names)
    const routeKey = useMemo(() => {
        const direct =
            params.slug ||
            params.school ||
            params.schoolSlug ||
            params.id ||
            params.schoolId ||
            params.chapter ||
            '';
        const first = direct || Object.values(params || {})[0] || '';
        return String(first || '').trim();
    }, [params]);

    const auth = useMemo(() => getAuth(), []);
    const functions = useMemo(() => getFunctions(), []);
    const createUserAccount = useMemo(() => httpsCallable(functions, 'createUserAccount'), [functions]);
    const deleteUserAccount = useMemo(() => httpsCallable(functions, 'deleteUserAccount'), [functions]);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentUserName, setCurrentUserName] = useState('');

    const [globalUserRole, setGlobalUserRole] = useState("");
    const isCorporateUser = useMemo(() => isCorporateRole(globalUserRole), [globalUserRole]);
    const isNationalEditorUser = useMemo(() => isNationalEditorRole(globalUserRole), [globalUserRole]);

    const [schools, setSchools] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(true);

    const [schoolId, setSchoolId] = useState('');
    const [schoolDoc, setSchoolDoc] = useState(null);
    const [loadingSchool, setLoadingSchool] = useState(true);

    // Tab is persisted to the URL (?tab=) and localStorage so a refresh or a
    // browser "back" from the talk editor returns here, not to the Home tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTabState] = useState(() => {
        const fromUrl = searchParams.get('tab');
        if (fromUrl && CHAPTER_TABS.includes(fromUrl)) return fromUrl;
        try {
            const fromStore = localStorage.getItem('cd:activeTab');
            if (fromStore && CHAPTER_TABS.includes(fromStore)) return fromStore;
        } catch {
            // localStorage unavailable
        }
        return 'home';
    });
    const setTab = useCallback((next) => {
        if (!CHAPTER_TABS.includes(next)) return;
        setTabState(next);
        try {
            localStorage.setItem('cd:activeTab', next);
        } catch {
            // ignore
        }
        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set('tab', next);
                return p;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    useEffect(() => {
        if (searchParams.get('tab') !== tab) {
            setSearchParams(
                (prev) => {
                    const p = new URLSearchParams(prev);
                    p.set('tab', tab);
                    return p;
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Home
    const [active, setActive] = useState(true);
    const [savingHome, setSavingHome] = useState(false);
    const [homeStatus, setHomeStatus] = useState('');
    const [ctaGroupMe, setCtaGroupMe] = useState('');
    const [ctaEmail, setCtaEmail] = useState('');
    const [ctaInstagram, setCtaInstagram] = useState('');
    const [chapterWebsite, setChapterWebsite] = useState('');
    const [schoolDisplayName, setSchoolDisplayName] = useState('');
    const [leaderNotes, setLeaderNotes] = useState('');
    const [chapterAnnouncements, setChapterAnnouncements] = useState('');
    // Internal notes (like National: individual notes that can be dismissed)
    const [internalNotes, setInternalNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesStatus, setNotesStatus] = useState('');
    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');

    // Talks/Semesters
    const [semesters, setSemesters] = useState([]);
    const [selectedSemesterId, setSelectedSemesterId] = useState('');
    const selectedSemester = useMemo(
        () => semesters.find((s) => s.id === selectedSemesterId) || null,
        [semesters, selectedSemesterId]
    );

    const [savingTalks, setSavingTalks] = useState(false);
    const [talksStatus, setTalksStatus] = useState('');

    // Users
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [memberStatus, setMemberStatus] = useState('');
    const [addEmail, setAddEmail] = useState('');
    const [addName, setAddName] = useState('');
    const [addRole, setAddRole] = useState('member');
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    // Toast
    const [toast, setToast] = useState({ open: false, message: '', kind: 'info' });
    const toastTimerRef = useRef(null);


    const showToast = useCallback((message, kind = 'info', ms = 2400) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ open: true, message, kind });
        toastTimerRef.current = setTimeout(() => {
            setToast((t) => ({ ...t, open: false }));
        }, ms);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // Auth
    useEffect(() => {
        let canceled = false;

        const unsub = onAuthStateChanged(auth, async (u) => {
            setCurrentUser(u || null);

            if (!u) {
                setGlobalUserRole("");
                setCurrentUserName('');
                return;
            }

            try {
                const role = await getGlobalRoleForAuthUser(db, u);
                if (canceled) return;
                setGlobalUserRole(role);
            } catch (e) {
                console.error("ChapterDashboard: failed to load user role", e);
                if (!canceled) setGlobalUserRole("");
            }

            try {
                const nm = await getDisplayNameForAuthUser(db, u);
                if (!canceled) setCurrentUserName(nm || (u?.displayName || u?.email || ''));
            } catch (e) {
                if (!canceled) setCurrentUserName(u?.displayName || u?.email || '');
            }
        });

        return () => {
            canceled = true;
            unsub();
        };
    }, [auth]);

    // Load schools list (for nice labels + slug mapping)
    useEffect(() => {
        let canceled = false;
        async function run() {
            try {
                setLoadingSchools(true);
                const snap = await getDocs(collection(db, 'schools'));
                if (canceled) return;
                const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
                rows.sort((a, b) => labelSchool(a).localeCompare(labelSchool(b)));
                setSchools(rows);
            } catch (e) {
                console.error('ChapterDashboard: failed to load schools', e);
            } finally {
                setLoadingSchools(false);
            }
        }
        run();
        return () => {
            canceled = true;
        };
    }, []);

    // Resolve school doc from URL (doc id OR slug)
    useEffect(() => {
        let canceled = false;
        async function run() {
            if (!routeKey) {
                setSchoolId('');
                return;
            }
            try {
                // 1) doc id
                const directRef = doc(db, 'schools', routeKey);
                const directSnap = await getDoc(directRef);
                if (canceled) return;
                if (directSnap.exists()) {
                    setSchoolId(routeKey);
                    return;
                }

                // 2) slug
                const qs = await getDocs(query(collection(db, 'schools'), where('slug', '==', routeKey)));
                if (canceled) return;
                if (!qs.empty) {
                    setSchoolId(qs.docs[0].id);
                    return;
                }

                setSchoolId('');
            } catch (e) {
                console.error('ChapterDashboard: failed to resolve school from route', e);
                setSchoolId('');
            }
        }
        run();
        return () => {
            canceled = true;
        };
    }, [routeKey]);

    // Load school doc
    useEffect(() => {
        let canceled = false;
        async function run() {
            if (!schoolId) {
                setSchoolDoc(null);
                setSemesters([]);
                setSelectedSemesterId('');
                return;
            }
            try {
                setLoadingSchool(true);
                const ref = doc(db, 'schools', schoolId);
                const snap = await getDoc(ref);
                if (canceled) return;
                if (!snap.exists()) {
                    setSchoolDoc(null);
                    setSemesters([]);
                    setSelectedSemesterId('');
                    return;
                }

                const data = snap.data() || {};
                const docObj = { id: schoolId, ...data };
                setSchoolDoc(docObj);
                setSchoolDisplayName(String(data.displayName || data.name || data.schoolName || ''));
                setLeaderNotes(String(data.leaderNotes || ''));
                setChapterAnnouncements(String(data.announcements || ''));

                // Internal notes (backward compat: if legacy leaderNotes string exists and internalNotes is empty)
                const rawNotes = Array.isArray(data.internalNotes) ? data.internalNotes : [];
                const normalizedNotes = rawNotes
                    .map((n, idx) => ({
                        id: n.id || n.noteId || `note_${idx}`,
                        text: String(n.text || n.note || n.message || ''),
                        authorName: String(n.authorName || n.author || n.userName || ''),
                        authorEmail: String(n.authorEmail || n.email || ''),
                        authorUid: String(n.authorUid || n.uid || ''),
                        createdAt: String(n.createdAt || ''),
                        dismissed: !!n.dismissed,
                        dismissedAt: String(n.dismissedAt || ''),
                        dismissedBy: String(n.dismissedBy || ''),
                    }))
                    .filter((n) => n.text.trim().length);

                if (!normalizedNotes.length && String(data.leaderNotes || '').trim().length) {
                    normalizedNotes.push({
                        id: 'legacy_leaderNotes',
                        text: String(data.leaderNotes || '').trim(),
                        authorName: 'Legacy note',
                        authorEmail: '',
                        authorUid: '',
                        createdAt: String(data.updatedAt || data.createdAt || ''),
                        dismissed: false,
                        dismissedAt: '',
                        dismissedBy: '',
                    });
                }

                // Show newest first
                normalizedNotes.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
                setInternalNotes(normalizedNotes);

                // Home
                setActive(typeof data.active === 'boolean' ? data.active : true);
                setCtaGroupMe(String(data.ctaGroupMe || data.groupLink || ''));
                setCtaEmail(String(data.ctaEmail || data.emailListLink || ''));
                setCtaInstagram(String(data.ctaInstagram || data.instagramUrl || ''));
                setChapterWebsite(String(data.chapterWebsite || data.website || ''));

                // Semesters
                const sems = coerceSemestersFromSchoolDoc(data);
                setSemesters(sems);
                setSelectedSemesterId((prev) => {
                    if (prev && sems.some((s) => s.id === prev)) return prev;
                    return sems[0]?.id || '';
                });
            } catch (e) {
                console.error('ChapterDashboard: failed to load school doc', e);
            } finally {
                setLoadingSchool(false);
            }
        }
        run();
        return () => {
            canceled = true;
        };
    }, [schoolId]);


    // Load members (used for permissions + Users tab)
    useEffect(() => {
        let canceled = false;

        async function run() {
            if (!schoolId) return;

            try {
                // Only show the loading spinner/message on the Users tab.
                if (tab === 'users') setLoadingMembers(true);
                setMemberStatus('');

                // Preferred schema: schools/{schoolId}/members
                const subSnap = await getDocs(collection(db, 'schools', schoolId, 'members'));
                if (canceled) return;

                if (!subSnap.empty) {
                    const rows = subSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
                    rows.sort((a, b) => {
                        const rank = { president: 0, leader: 1, member: 2 };
                        const ra = normalizeRole(a.role);
                        const rb = normalizeRole(b.role);
                        if (rank[ra] !== rank[rb]) return rank[ra] - rank[rb];
                        return String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''));
                    });
                    setMembers(rows);
                    return;
                }

                // Fallback: schools/{schoolId}.members array or other legacy formats
                const schRef = doc(db, 'schools', schoolId);
                const schSnap = await getDoc(schRef);
                if (canceled) return;
                const data = schSnap.data() || {};
                const coerced = coerceMembersFromSchoolDoc(data);
                setMembers(coerced);
            } catch (e) {
                console.error('ChapterDashboard: failed to load members', e);
                setMemberStatus('Error loading members. See console for details.');
                setMembers([]);
            } finally {
                if (tab === 'users') setLoadingMembers(false);
            }
        }

        run();
        return () => {
            canceled = true;
        };
    }, [schoolId, tab]);

    // If you switch away from Users tab, clear the loading indicator.
    useEffect(() => {
        if (tab !== 'users') setLoadingMembers(false);
    }, [tab]);

    // My role (based on members list)
    const myRole = useMemo(() => {
        // Corporate users act as presidents for all chapters.
        if (isCorporateUser) return 'president';
        // National editors can edit chapter content even if they aren't in the chapter roster.
        if (isNationalEditorUser) return 'leader';

        if (!currentUser || !members?.length) return 'member';

        const me = members.find(
            (m) =>
                (m.uid && currentUser.uid && m.uid === currentUser.uid) ||
                (m.email && currentUser.email && String(m.email).toLowerCase() === String(currentUser.email).toLowerCase())
        );

        return normalizeRole(me?.role);
    }, [currentUser, members, isCorporateUser, isNationalEditorUser]);

    const canEditTalks = useMemo(() => {
        if (isCorporateUser || isNationalEditorUser) return true;
        const r = normalizeRole(myRole);
        return r === 'president' || r === 'leader';
    }, [myRole, isCorporateUser, isNationalEditorUser]);

    const canEditHome = useMemo(() => {
        if (isCorporateUser || isNationalEditorUser) return true;
        const r = normalizeRole(myRole);
        return r === 'president' || r === 'leader';
    }, [myRole, isCorporateUser, isNationalEditorUser]);

    const schoolName = useMemo(() => {
        if (trim(schoolDisplayName)) return trim(schoolDisplayName);
        if (schoolDoc?.displayName || schoolDoc?.name || schoolDoc?.schoolName) {
            return schoolDoc.displayName || schoolDoc.name || schoolDoc.schoolName;
        }
        if (schoolId) {
            const row = schools.find((s) => s.id === schoolId);
            return row ? labelSchool(row) : schoolId;
        }
        return routeKey || 'Chapter';
    }, [schoolDoc, schoolId, schools, routeKey]);

    const publicPageUrl = useMemo(() => {
        // Adjust if your public chapter pages use a different route.
        // Keeping it stable and safe even if missing a slug.
        const slug = schoolDoc?.slug || routeKey;
        if (!slug) return '';
        return `/schools/${slug}`;
    }, [schoolDoc, routeKey]);

    const todayIso = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    const currentSemesterForWidgets = useMemo(() => {
        const sems = Array.isArray(semesters) ? semesters : [];
        if (!sems.length) return null;

        // Prefer a semester whose public window includes today (if both bounds exist)
        const inWindow = sems.find((s) => {
            const from = String(s.publicFrom || '').trim();
            const to = String(s.publicTo || '').trim();
            if (!from || !to) return false;
            return from <= todayIso && todayIso <= to;
        });
        if (inWindow) return inWindow;

        // Otherwise: most recent publicFrom (fallback to first)
        const byFrom = [...sems].sort((a, b) =>
            String(b.publicFrom || '').localeCompare(String(a.publicFrom || ''))
        );
        return byFrom[0] || sems[0] || null;
    }, [semesters, todayIso]);

    const totalTalksThisSemester = useMemo(() => {
        const sem = currentSemesterForWidgets;
        return sem?.events?.length ? sem.events.length : 0;
    }, [currentSemesterForWidgets]);

    const avgAttendanceThisSemester = useMemo(() => {
        const sem = currentSemesterForWidgets;
        const events = Array.isArray(sem?.events) ? sem.events : [];
        const nums = events
            .map((e) => {
                const n = Number(String(e?.attendanceCount ?? '').trim());
                return Number.isFinite(n) ? n : 0;
            })
            .filter((n) => n > 0);

        if (!nums.length) return null;
        const sum = nums.reduce((a, b) => a + b, 0);
        return sum / nums.length;
    }, [currentSemesterForWidgets]);

    const nextTalk = useMemo(() => {
        const sems = Array.isArray(semesters) ? semesters : [];
        const all = [];
        for (const s of sems) {
            for (const e of (s?.events || [])) {
                if (!e?.date) continue;
                const dt = toDateObj(e);
                if (!dt || Number.isNaN(dt.getTime())) continue;
                all.push({ ...e, _dt: dt, _semName: s?.name || '' });
            }
        }
        const now = new Date();
        const upcoming = all
            .filter((x) => x._dt.getTime() >= now.getTime())
            .sort((a, b) => a._dt.getTime() - b._dt.getTime());
        return upcoming[0] || null;
    }, [semesters]);

    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        // Update countdown every 30s
        const id = setInterval(() => setNowTick(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);

    const nextTalkCountdown = useMemo(() => {
        if (!nextTalk?._dt) return null;
        const diffMs = nextTalk._dt.getTime() - nowTick;
        if (diffMs <= 0) return { days: 0, hours: 0, mins: 0, totalMs: 0 };
        const totalMins = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMins / (60 * 24));
        const hours = Math.floor((totalMins - days * 60 * 24) / 60);
        const mins = totalMins - days * 60 * 24 - hours * 60;
        return { days, hours, mins, totalMs: diffMs };
    }, [nextTalk, nowTick]);

    // --------------------
    // Home actions
    // --------------------
    // Helper: Refetch the school doc and update local state (including Home fields)
    async function refetchSchoolDoc() {
        if (!schoolId) return null;
        try {
            let snap;
            try {
                snap = await getDocFromServer(doc(db, 'schools', schoolId));
            } catch (e) {
                // If offline or blocked, fall back to cached/local read
                console.warn('ChapterDashboard: getDocFromServer failed, falling back to getDoc', e);
                snap = await getDoc(doc(db, 'schools', schoolId));
            }
            if (!snap.exists()) return null;
            const data = snap.data() || {};
            const obj = { id: schoolId, ...data };
            setSchoolDoc(obj);
            // Keep Home state in sync with canonical doc
            setSchoolDisplayName(String(data.displayName || data.name || data.schoolName || ''));
            setLeaderNotes(String(data.leaderNotes || ''));
            setChapterAnnouncements(String(data.announcements || ''));
            setActive(typeof data.active === 'boolean' ? data.active : true);
            setCtaGroupMe(String(data.ctaGroupMe || data.groupLink || ''));
            setCtaEmail(String(data.ctaEmail || data.emailListLink || ''));
            setCtaInstagram(String(data.ctaInstagram || data.instagramUrl || ''));
            setChapterWebsite(String(data.chapterWebsite || data.website || ''));
            // Internal notes (keep in sync)
            const rawNotes = Array.isArray(data.internalNotes) ? data.internalNotes : [];
            const normalizedNotes = rawNotes
                .map((n, idx) => ({
                    id: n.id || n.noteId || `note_${idx}`,
                    text: String(n.text || n.note || n.message || ''),
                    authorName: String(n.authorName || n.author || n.userName || ''),
                    authorEmail: String(n.authorEmail || n.email || ''),
                    authorUid: String(n.authorUid || n.uid || ''),
                    createdAt: String(n.createdAt || ''),
                    dismissed: !!n.dismissed,
                    dismissedAt: String(n.dismissedAt || ''),
                    dismissedBy: String(n.dismissedBy || ''),
                }))
                .filter((n) => n.text.trim().length);
            normalizedNotes.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
            setInternalNotes(normalizedNotes);
            return obj;
        } catch (e) {
            console.error('ChapterDashboard: failed to refetch school doc', e);
            return null;
        }
    }

    const refreshInternalNotes = useCallback(async () => {
        if (!schoolId) return;
        setNotesLoading(true);
        setNotesStatus('');
        try {
            const fresh = await refetchSchoolDoc();
            const data = fresh || schoolDoc;
            const rawNotes = Array.isArray(data?.internalNotes) ? data.internalNotes : [];
            const normalizedNotes = rawNotes
                .map((n, idx) => ({
                    id: n.id || n.noteId || `note_${idx}`,
                    text: String(n.text || n.note || n.message || ''),
                    authorName: String(n.authorName || n.author || n.userName || ''),
                    authorEmail: String(n.authorEmail || n.email || ''),
                    authorUid: String(n.authorUid || n.uid || ''),
                    createdAt: String(n.createdAt || ''),
                    dismissed: !!n.dismissed,
                    dismissedAt: String(n.dismissedAt || ''),
                    dismissedBy: String(n.dismissedBy || ''),
                }))
                .filter((n) => n.text.trim().length);

            normalizedNotes.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
            setInternalNotes(normalizedNotes);
        } catch (e) {
            console.error('ChapterDashboard: failed to refresh internal notes', e);
            setNotesStatus('Error loading notes.');
        } finally {
            setNotesLoading(false);
        }
    }, [schoolId, schoolDoc]);

    const addInternalNote = useCallback(async () => {
        if (!schoolId) return;
        if (!canEditHome) {
            showToast('View-only: cannot add notes.', 'warn');
            return;
        }
        const txt = String(newNoteText || '').trim();
        if (!txt) {
            showToast('Write a note first.', 'warn');
            return;
        }

        try {
            const id = `note_${Date.now()}_${Math.random().toString(16).slice(2)}`;
            const note = {
                id,
                text: txt,
                authorName: String(currentUserName || currentUser?.displayName || currentUser?.email || 'Unknown'),
                authorEmail: String(currentUser?.email || ''),
                authorUid: String(currentUser?.uid || ''),
                createdAt: nowIso(),
                dismissed: false,
            };

            const next = [note, ...(Array.isArray(internalNotes) ? internalNotes : [])];
            setInternalNotes(next);

            await updateDoc(doc(db, 'schools', schoolId), {
                internalNotes: next,
                updatedAt: nowIso(),
            });

            setShowAddNoteModal(false);
            setNewNoteText('');
            showToast('Note added.', 'success');
        } catch (e) {
            console.error('ChapterDashboard: failed to add internal note', e);
            showToast('Error adding note.', 'error');
        }
    }, [schoolId, canEditHome, newNoteText, currentUserName, currentUser, internalNotes, showToast]);

    const dismissInternalNote = useCallback(async (noteId) => {
        if (!schoolId) return;
        if (!canEditHome) {
            showToast('View-only: cannot dismiss notes.', 'warn');
            return;
        }

        try {
            const who = String(currentUserName || currentUser?.displayName || currentUser?.email || 'Unknown');
            const next = (Array.isArray(internalNotes) ? internalNotes : []).map((n) =>
                n.id === noteId
                    ? { ...n, dismissed: true, dismissedAt: nowIso(), dismissedBy: who }
                    : n
            );
            setInternalNotes(next);

            await updateDoc(doc(db, 'schools', schoolId), {
                internalNotes: next,
                updatedAt: nowIso(),
            });

            showToast('Dismissed.', 'success');
        } catch (e) {
            console.error('ChapterDashboard: failed to dismiss note', e);
            showToast('Error dismissing note.', 'error');
        }
    }, [schoolId, canEditHome, internalNotes, currentUserName, currentUser, showToast]);

    const saveHome = useCallback(async () => {
        if (!schoolId) return;
        if (!canEditHome) {
            showToast("View-only: cannot save Home.", "warn");
            return;
        }
        if (loadingSchool || !schoolDoc) {
            showToast("Chapter not ready to save yet.", "warn");
            return;
        }

        setSavingHome(true);
        setHomeStatus('');

        try {
            const safeDisplayName =
                firstNonEmpty(trim(schoolDisplayName), schoolDoc?.displayName, schoolDoc?.name, schoolDoc?.schoolName);

            const safeGroup =
                firstNonEmpty(trim(ctaGroupMe), schoolDoc?.ctaGroupMe, schoolDoc?.groupLink);

            const safeEmail =
                firstNonEmpty(trim(ctaEmail), schoolDoc?.ctaEmail, schoolDoc?.emailListLink);

            const safeIg =
                firstNonEmpty(trim(ctaInstagram), schoolDoc?.ctaInstagram, schoolDoc?.instagramUrl);

            const safeWeb =
                firstNonEmpty(trim(chapterWebsite), schoolDoc?.chapterWebsite, schoolDoc?.website);

            // const safeLeaderNotes =
            //     String(leaderNotes ?? "").length ? leaderNotes : String(schoolDoc?.leaderNotes || "");

            const safeAnnouncements =
                String(chapterAnnouncements ?? "").length ? chapterAnnouncements : String(schoolDoc?.announcements || "");

            const saveStamp = nowIso();

            await updateDoc(doc(db, "schools", schoolId), {
                displayName: safeDisplayName,
                // leaderNotes: safeLeaderNotes, // REMOVE: leaderNotes now managed via internalNotes
                announcements: safeAnnouncements,
                active: !!active,
                ctaGroupMe: safeGroup,
                ctaEmail: safeEmail,
                ctaInstagram: safeIg,
                chapterWebsite: safeWeb,

                // legacy aliases
                name: safeDisplayName,
                schoolName: safeDisplayName,
                groupLink: safeGroup,
                emailListLink: safeEmail,
                instagramUrl: safeIg,
                website: safeWeb,

                updatedAt: saveStamp,
            });

            const fresh = await refetchSchoolDoc();
            const freshUpdatedAt = String(fresh?.updatedAt || '').trim();
            if (!freshUpdatedAt) throw new Error('Save verification failed: updatedAt missing after save.');
            if (freshUpdatedAt !== saveStamp) {
                throw new Error(`Save verification failed: server updatedAt mismatch. expected=${saveStamp} got=${freshUpdatedAt}`);
            }

            setHomeStatus('Saved!');
            showToast('Home saved.', 'success');
            setTimeout(() => setHomeStatus(''), 1500);
        } catch (e) {
            console.error('ChapterDashboard: failed to save home settings', e);
            showToast('Error saving Home.', 'error');
            setHomeStatus('Error saving. See console for details.');
        } finally {
            setSavingHome(false);
        }
    }, [
        schoolId,
        canEditHome,
        loadingSchool,
        schoolDoc,
        schoolDisplayName,
        ctaGroupMe,
        ctaEmail,
        ctaInstagram,
        chapterWebsite,
        // leaderNotes, // not needed anymore
        chapterAnnouncements,
        active,
        showToast,
    ]);

    // --------------------
    // Semester/Talk actions
    // --------------------
    function updateSemesterField(semId, field, value) {
        setSemesters((prev) =>
            prev.map((s) => (s.id === semId ? { ...s, [field]: value } : s))
        );
    }

    function updateEvent(semId, idx, field, value) {
        setSemesters((prev) =>
            prev.map((s) => {
                if (s.id !== semId) return s;
                const nextEvents = [...(s.events || [])];
                nextEvents[idx] = { ...(nextEvents[idx] || {}), [field]: value };
                return { ...s, events: nextEvents };
            })
        );
    }

    function addEventToSemester(semId) {
        setSemesters((prev) =>
            prev.map((s) => (s.id === semId ? { ...s, events: [...(s.events || []), blankEvent()] } : s))
        );
    }

    function removeEventFromSemester(semId, idx) {
        setSemesters((prev) =>
            prev.map((s) => {
                if (s.id !== semId) return s;
                const nextEvents = (s.events || []).filter((_, i) => i !== idx);
                return { ...s, events: nextEvents };
            })
        );
    }

    function addSemester() {
        const name = fallbackSemesterName();
        const sem = normalizeSemester({ name, id: semesterIdFromName(`${name}-${Date.now()}`) });
        setSemesters((prev) => [sem, ...prev]);
        setSelectedSemesterId(sem.id);
    }

    function removeSemester(semId) {
        setSemesters((prev) => {
            const next = prev.filter((s) => s.id !== semId);
            const safe = next.length ? next : [normalizeSemester({ id: 'default', name: fallbackSemesterName() })];
            return safe;
        });
        setSelectedSemesterId((prev) => {
            if (prev !== semId) return prev;
            const next = semesters.filter((s) => s.id !== semId);
            return next[0]?.id || 'default';
        });
    }

    const saveTalksData = useCallback(async () => {
        if (!schoolId) return;
        if (!canEditTalks) return;
        if (loadingSchool || !schoolDoc) {
            showToast("Chapter not ready to save yet.", "warn");
            return;
        }

        setSavingTalks(true);
        setTalksStatus('');

        try {
            const saveStamp = nowIso();

            const normalized = (semesters || []).map((s) => {
                const sem = normalizeSemester(s);
                return {
                    ...sem,
                    announcements: sem.announcements || '',
                    events: sortEvents(sem.events || []).map((e) => ({
                        title: trim(e.title) || '',
                        description: trim(e.description) || '',
                        date: e.date || '',
                        time: e.time || '',
                        location: trim(e.location) || '',
                        talkLink: trim(e.talkLink) || '',
                        talkNotesLink: trim(e.talkNotesLink) || '',
                        talkRecordingLink: trim(e.talkRecordingLink) || '',
                        attendanceCount: String(e.attendanceCount ?? '').trim(),
                        attendanceNotes: String(e.attendanceNotes ?? ''),
                    })),
                };
            });

            await updateDoc(doc(db, 'schools', schoolId), {
                semesters: normalized,
                updatedAt: saveStamp,
            });

            const fresh = await refetchSchoolDoc();
            const freshUpdatedAt = String(fresh?.updatedAt || '').trim();
            if (!freshUpdatedAt) throw new Error('Save verification failed: updatedAt missing after save.');
            if (freshUpdatedAt !== saveStamp) {
                throw new Error(`Save verification failed: server updatedAt mismatch. expected=${saveStamp} got=${freshUpdatedAt}`);
            }

            setSemesters(normalized);
            setSelectedSemesterId((prev) => (prev && normalized.some((x) => x.id === prev) ? prev : (normalized[0]?.id || '')));

            setTalksStatus('Saved!');
            showToast('Talks saved.', 'success');
            setTimeout(() => setTalksStatus(''), 1500);
        } catch (e) {
            console.error('ChapterDashboard: failed to save talks', e);
            showToast('Error saving Talks.', 'error');
            setTalksStatus('Error saving. See console for details.');
        } finally {
            setSavingTalks(false);
        }
    }, [schoolId, canEditTalks, loadingSchool, schoolDoc, semesters, showToast]);

    // Cmd/Ctrl+S to save the current tab (Home or Talks)
    const tabRef = useRef(tab);
    const saveHomeRef = useRef(saveHome);
    const saveTalksRef = useRef(saveTalksData);

    useEffect(() => {
        tabRef.current = tab;
    }, [tab]);

    useEffect(() => {
        saveHomeRef.current = saveHome;
    }, [saveHome]);

    useEffect(() => {
        saveTalksRef.current = saveTalksData;
    }, [saveTalksData]);

    useEffect(() => {
        const onKeyDown = (e) => {
            const key = String(e.key || '').toLowerCase();
            const mod = e.metaKey || e.ctrlKey;
            if (!mod || key !== 's') return;

            e.preventDefault();

            if (!schoolId || loadingSchool || !schoolDoc) {
                showToast('Chapter not ready to save yet.', 'warn');
                return;
            }

            const curTab = tabRef.current;
            if (curTab === 'home') {
                if (!canEditHome) {
                    showToast('View-only: cannot save Home.', 'warn');
                    return;
                }
                showToast('Saving…', 'info', 900);
                saveHomeRef.current?.();
                return;
            }

            if (curTab === 'talks') {
                if (!canEditTalks) {
                    showToast('View-only: cannot save Talks.', 'warn');
                    return;
                }
                showToast('Saving…', 'info', 900);
                saveTalksRef.current?.();
                return;
            }

            showToast('Nothing to save on Users.', 'info');
        };

        // Capture phase so it works inside inputs/textareas
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [
        schoolId,
        loadingSchool,
        schoolDoc,
        canEditHome,
        canEditTalks,
        showToast,
    ]);

    // Helper to ensure a user profile doc exists for a given email (if missing), and update invitedChapters.
    // This ALSO attempts to create a Firebase Auth user (email/password) using a secondary app, matching users.js.
    async function ensureUserProfileByEmail(emailRaw, nameRaw = '', roleRaw = 'member') {
        const emailRawStr = String(emailRaw || '').trim();
        const emailLower = emailRawStr.toLowerCase();
        const email = emailLower; // canonical stored email
        if (!emailRawStr) return;

        const name = String(nameRaw || '').trim();
        const role = normalizeRole(roleRaw);

        // Try to find an existing user doc by email (support multiple legacy field names)
        const fields = ['email', 'Email', 'userEmail'];
        let existingDocId = null;

        for (const field of fields) {
            try {
                let qs = await getDocs(query(collection(db, 'users'), where(field, '==', emailRawStr), limit(1)));
                if (qs.empty && emailRawStr !== emailLower) {
                    qs = await getDocs(query(collection(db, 'users'), where(field, '==', emailLower), limit(1)));
                }
                if (!qs.empty) {
                    existingDocId = qs.docs[0].id;
                    break;
                }
            } catch (e) {
                // ignore and continue
            }
        }

        const now = nowIso();

        // If we already have a Firestore user doc, update invitedChapters and fill missing fields.
        // BUT: if the existing doc is an email-based stub (common in older data) and it indicates
        // that Auth was never created, attempt to create an Auth user now (matching users.js).
        if (existingDocId) {
            let ref;
            let cur = {};
            try {
                ref = doc(db, 'users', existingDocId);
                const snap = await getDoc(ref);
                cur = snap.exists() ? (snap.data() || {}) : {};
            } catch (e) {
                // If we can't read the existing doc, still proceed to the normal update path below.
                ref = doc(db, 'users', existingDocId);
                cur = {};
            }

            const invitedChapters = (cur.invitedChapters && typeof cur.invitedChapters === 'object' && !Array.isArray(cur.invitedChapters))
                ? { ...cur.invitedChapters }
                : {};

            invitedChapters[schoolId] = {
                role,
                addedAt: now,
                addedBy: String(currentUser?.uid || ''),
            };

            // Decide whether we should try to create an Auth user even though a Firestore doc exists.
            // If the docId looks like an email (contains "@") and we don't have a uid/authUid recorded,
            // we treat it as a Firestore-only stub.
            const docIdLooksLikeEmail = String(existingDocId || '').includes('@');
            const recordedUid = String(cur.authUid || cur.uid || '').trim();
            const needsAuthCreation = !!cur.needsAuthCreation || (docIdLooksLikeEmail && !recordedUid && !cur.authAlreadyExists);

            if (needsAuthCreation) {
                try {
                    let secondaryApp;
                    try {
                        secondaryApp = getApp('Secondary');
                    } catch {
                        secondaryApp = initializeAppSecondary(firebaseConfig, 'Secondary');
                    }

                    const secondaryAuth = getAuthSecondary(secondaryApp);

                    // Generate a temporary password (>= 12 chars). User can reset later.
                    const tempPassword = `Sole-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

                    const { user } = await createUserWithEmailAndPassword(secondaryAuth, email, tempPassword);
                    const createdUid = String(user?.uid || '');
                    if (!createdUid) throw new Error('Auth created but uid missing.');

                    // Write the canonical user doc keyed by uid
                    await setDoc(
                        doc(db, 'users', createdUid),
                        {
                            email: String(user.email || email),
                            name: name || String(cur.name || cur.displayName || ''),
                            displayName: name || String(cur.displayName || cur.name || ''),
                            role: role,
                            userType: String(cur.userType || 'member'),
                            invitedChapters,
                            createdAt: String(cur.createdAt || now),
                            updatedAt: now,
                        },
                        { merge: true }
                    );

                    // Update the stub doc so we have a link back to the auth uid (do not delete it)
                    await setDoc(
                        ref,
                        {
                            email: email,
                            authUid: createdUid,
                            needsAuthCreation: false,
                            authAlreadyExists: false,
                            migratedToUid: createdUid,
                            updatedAt: now,
                            invitedChapters,
                        },
                        { merge: true }
                    );

                    try {
                        await secondaryAuth.signOut();
                    } catch {
                        // ignore
                    }
                    try {
                        await deleteApp(secondaryApp);
                    } catch {
                        // ignore
                    }

                    return;
                } catch (e) {
                    const code = String(e?.code || '');
                    console.warn('ChapterDashboard: failed to create Auth user from existing stub', e);

                    // If the auth user already exists, we still update the Firestore stub invite.
                    await setDoc(
                        ref,
                        {
                            email: email,
                            invitedChapters,
                            updatedAt: now,
                            needsAuthCreation: code !== 'auth/email-already-in-use',
                            authAlreadyExists: code === 'auth/email-already-in-use',
                            authCreateErrorCode: code || null,
                            authCreateErrorMessage: String(e?.message || ''),
                        },
                        { merge: true }
                    );

                    try {
                        showToast(
                            `Could not create an Authentication user for ${email}. (${code || 'unknown error'}) See console.`,
                            'error',
                            5000
                        );
                    } catch {
                        // ignore
                    }

                    return;
                }
            }

            // Normal case: just update the existing profile doc.
            try {
                await updateDoc(ref, {
                    ...(name && !String(cur.name || cur.displayName || cur.fullName || '').trim() ? { name, displayName: name } : {}),
                    email: String(cur.email || '').trim() ? cur.email : email,
                    invitedChapters,
                    updatedAt: now,
                });
            } catch (e) {
                console.warn('ChapterDashboard: ensureUserProfileByEmail update failed', e);
                // If updateDoc fails (permissions/rules), fall back to merge setDoc.
                try {
                    await setDoc(
                        ref,
                        {
                            ...(name ? { name, displayName: name } : {}),
                            email: String(cur.email || '').trim() ? cur.email : email,
                            invitedChapters,
                            updatedAt: now,
                        },
                        { merge: true }
                    );
                } catch (e2) {
                    console.warn('ChapterDashboard: ensureUserProfileByEmail fallback setDoc failed', e2);
                }
            }

            return;
        }

        // No Firestore user doc found. Attempt to create an Auth user (like users.js) and then create users/{uid}.
        // NOTE: this requires Email/Password to be enabled in Firebase Auth.
        try {
            let secondaryApp;
            try {
                secondaryApp = getApp('Secondary');
            } catch {
                secondaryApp = initializeAppSecondary(firebaseConfig, 'Secondary');
            }

            const secondaryAuth = getAuthSecondary(secondaryApp);

            // Generate a temporary password (>= 12 chars). User can reset later.
            const tempPassword = `Sole-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

            const { user } = await createUserWithEmailAndPassword(secondaryAuth, email, tempPassword);
            const createdUid = String(user?.uid || '');
            if (!createdUid) throw new Error('Auth created but uid missing.');

            await setDoc(
                doc(db, 'users', createdUid),
                {
                    email: String(user.email || email),
                    name: name || '',
                    displayName: name || '',
                    role: role,
                    userType: 'member',
                    invitedChapters: {
                        [schoolId]: {
                            role,
                            addedAt: now,
                            addedBy: String(currentUser?.uid || ''),
                        },
                    },
                    createdAt: now,
                    updatedAt: now,
                },
                { merge: true }
            );

            // Clean up: do not stay signed in as the created user; remove the Secondary app.
            try {
                await secondaryAuth.signOut();
            } catch {
                // ignore
            }
            try {
                await deleteApp(secondaryApp);
            } catch {
                // ignore
            }

            return;
        } catch (e) {
            const code = String(e?.code || '');
            console.warn('ChapterDashboard: failed to create Auth user', e);

            // If the email already exists in Auth, we still create/update a Firestore stub so the invite isn't lost.
            // (We cannot resolve the uid from the client in that case.)
            const needsAuthCreation = code !== 'auth/email-already-in-use';

            try {
                await setDoc(
                    doc(db, 'users', emailLower),
                    {
                        email: emailLower,
                        name: name || '',
                        displayName: name || '',
                        role: role,
                        userType: 'member',
                        invitedChapters: {
                            [schoolId]: {
                                role,
                                addedAt: now,
                                addedBy: String(currentUser?.uid || ''),
                            },
                        },
                        createdAt: now,
                        updatedAt: now,
                        needsAuthCreation,
                        authAlreadyExists: code === 'auth/email-already-in-use',
                        authCreateErrorCode: code || null,
                        authCreateErrorMessage: String(e?.message || ''),
                    },
                    { merge: true }
                );
            } catch (e2) {
                console.warn('ChapterDashboard: fallback Firestore user write failed', e2);
            }

            try {
                showToast(
                    `Could not create an Authentication user for ${emailLower}. (${code || 'unknown error'}) See console.`,
                    'error',
                    5000
                );
            } catch {
                // ignore
            }
        }
    }
    // --------------------
    // Member actions
    // --------------------
    async function addMember() {
        if (!schoolId) return;
        if (!canAddMembers(myRole, isCorporateUser)) {
            setMemberStatus('You do not have permission to add members.');
            showToast('View-only: cannot add members.', 'warn');
            return;
        }

        const email = String(addEmail || '').trim();
        const name = String(addName || '').trim();
        const role = normalizeRole(addRole);

        if (!email) {
            setMemberStatus('Please enter an email.');
            return;
        }

        setMemberStatus('');

        try {
            const ref = doc(collection(db, 'schools', schoolId, 'members'));
            // const memberDocId = ref.id; // Not used after this, so not needed
            await setDoc(ref, {
                email,
                name: name || '',
                role,
                createdAt: nowIso(),
                createdBy: currentUser?.uid || '',
                uid: '', // Store the auth uid, to be filled after account creation
            });

            // Ensure a user profile doc exists (now attempts to create an Auth account if missing).
            // Create/ensure the Auth user + canonical Firestore profile via callable function.
            // Then attach this chapter invite onto the canonical users/{uid} doc.
            let createdUid = '';
            try {
                const resp = await createUserAccount({
                    email: String(email).trim(),
                    name: String(name || '').trim(),
                    role,
                    userType: 'member',
                });
                createdUid = String(resp?.data?.uid || '').trim();
                // After successful creation, best-effort update to store the uid on the member doc
                if (createdUid) {
                    try {
                        await updateDoc(ref, { uid: createdUid, updatedAt: nowIso() });
                    } catch (e) {
                        console.warn('ChapterDashboard: failed to write member uid', e);
                    }
                }
            } catch (e) {
                console.error('ChapterDashboard: createUserAccount failed', e);
                showToast('Member added, but failed to create Authentication account. See console.', 'warn', 5000);
            }

            // Always maintain invitedChapters mapping on the user profile (best-effort)
            try {
                const invite = {
                    role,
                    addedAt: nowIso(),
                    addedBy: String(currentUser?.uid || ''),
                };

                const emailLower = String(email).trim().toLowerCase();

                if (createdUid) {
                    await setDoc(
                        doc(db, 'users', createdUid),
                        {
                            email: emailLower,
                            ...(name ? { name, displayName: name } : {}),
                            invitedChapters: { [schoolId]: invite },
                            updatedAt: nowIso(),
                        },
                        { merge: true }
                    );
                } else {
                    // fallback: find user doc by email
                    const qs = await getDocs(query(collection(db, 'users'), where('email', '==', emailLower), limit(1)));
                    if (!qs.empty) {
                        await setDoc(
                            doc(db, 'users', qs.docs[0].id),
                            { invitedChapters: { [schoolId]: invite }, updatedAt: nowIso() },
                            { merge: true }
                        );
                    } else {
                        // legacy stub by email
                        await setDoc(
                            doc(db, 'users', emailLower),
                            {
                                email: emailLower,
                                ...(name ? { name, displayName: name } : {}),
                                invitedChapters: { [schoolId]: invite },
                                updatedAt: nowIso(),
                            },
                            { merge: true }
                        );
                    }
                }
            } catch (e) {
                console.warn('ChapterDashboard: failed to update invitedChapters for user', e);
            }

            // If we had to fall back to a Firestore stub (email-doc), surface that so it’s not confusing.
            try {
                const stubSnap = await getDoc(doc(db, 'users', String(email || '').trim().toLowerCase()));
                if (stubSnap.exists() && stubSnap.data()?.needsAuthCreation) {
                    showToast('Member added, but Auth account was NOT created. Open console for the exact error.', 'warn', 5000);
                }
            } catch (e) {
                // ignore
            }

            setAddEmail('');
            setAddName('');
            setAddRole('member');
            setShowAddMemberModal(false);

            const snap = await getDocs(collection(db, 'schools', schoolId, 'members'));
            const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
            setMembers(rows);

            setMemberStatus('Member added.');
            showToast('Member added.', 'success');
            setTimeout(() => setMemberStatus(''), 1500);
        } catch (e) {
            console.error('ChapterDashboard: failed to add member', e);
            setMemberStatus('Error adding member. See console for details.');
        }
    }

    async function updateMemberRole(memberId, nextRole) {
        if (!schoolId) return;
        if (!canManageMembers(myRole, isCorporateUser)) return;
        const role = normalizeRole(nextRole);
        try {
            await updateDoc(doc(db, 'schools', schoolId, 'members', memberId), { role });
            setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role } : m)));
        } catch (e) {
            console.error('ChapterDashboard: failed to update member role', e);
            setMemberStatus('Error updating role. See console for details.');
        }
    }

    async function removeMember(member) {
        if (!schoolId) return;
        if (!canManageMembers(myRole, isCorporateUser)) return;

        const memberId = String(member?.id || '').trim();
        const emailRaw = String(member?.email || '').trim();
        const email = emailRaw.toLowerCase();
        const memberUid = String(member?.uid || member?.authUid || '').trim();

        if (!memberId) return;

        // IMPORTANT: for Chapter users, "Remove" should delete the account (Auth + profile),
        // not just remove them from this chapter.
        const ok = window.confirm(
            `Delete ${emailRaw || 'this user'} completely? This will remove them from this chapter AND delete their Authentication account.`
        );
        if (!ok) return;

        try {
            // 1) Remove the chapter membership doc
            await deleteDoc(doc(db, 'schools', schoolId, 'members', memberId));
            setMembers((prev) => prev.filter((m) => m.id !== memberId));

            // 2) Resolve the canonical uid + any Firestore user-doc ids we should clean up
            let candidateUid = memberUid;
            let userDocId = '';

            // 2a) If we have an email-stub doc users/{email}, use it to resolve authUid/migratedToUid
            if (email) {
                try {
                    const legacyRef = doc(db, 'users', email);
                    const legacySnap = await getDoc(legacyRef);
                    if (legacySnap.exists()) {
                        userDocId = email;
                        const data = legacySnap.data() || {};
                        const uidFromStub = String(data.authUid || data.migratedToUid || data.uid || '').trim();
                        if (uidFromStub) candidateUid = uidFromStub;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // 2b) If still no uid, try query users by email (common schema is users/{uid} with email field)
            if (!candidateUid && email) {
                try {
                    const qs = await getDocs(query(collection(db, 'users'), where('email', '==', email), limit(1)));
                    if (!qs.empty) {
                        userDocId = qs.docs[0].id;
                        const data = qs.docs[0].data() || {};
                        const uidFromDoc = String(data.authUid || data.uid || '').trim();
                        if (uidFromDoc) candidateUid = uidFromDoc;
                        if (!candidateUid && String(qs.docs[0].id || '').length > 10 && !String(qs.docs[0].id).includes('@')) {
                            candidateUid = qs.docs[0].id;
                        }
                    }
                } catch (e) {
                    // ignore
                }
            }

            if (!candidateUid) {
                showToast(
                    `Removed from chapter, but could not delete Authentication account (missing uid).`,
                    'warn',
                    5000
                );
                return;
            }

            // 3) Delete Authentication account server-side
            // (This MUST delete Firebase Auth, not just Firestore.)
            await deleteUserAccount({ uid: candidateUid, email });

            // 4) Clean up Firestore profile docs client-side (best-effort)
            try {
                const deletes = [];
                // canonical uid doc
                deletes.push(deleteDoc(doc(db, 'users', candidateUid)));
                // legacy email stub (if present)
                if (email) deletes.push(deleteDoc(doc(db, 'users', email)));
                // queried doc fallback
                if (userDocId && userDocId !== candidateUid && userDocId !== email) {
                    deletes.push(deleteDoc(doc(db, 'users', userDocId)));
                }
                await Promise.allSettled(deletes);
            } catch (e) {
                console.warn('ChapterDashboard: Firestore cleanup after deleteUserAccount failed', e);
            }

            showToast('User deleted.', 'success');
        } catch (e) {
            console.error('ChapterDashboard: failed to delete user', e);
            setMemberStatus('Error deleting user. See console for details.');
            showToast('Error deleting user.', 'error');
        }
    }

    // --------------------
    // Render helpers
    // --------------------
    const shellNote = useMemo(() => {
        if (!routeKey) return 'Missing school slug in URL.';
        if (!schoolId) return `No school found for “${routeKey}”.`;
        return '';
    }, [routeKey, schoolId]);
    // Keep sidebar/top offsets aligned with the actual (dynamic) navbar height.
    // Navbar can hide/show while scrolling; we reflect its *visible* height into a CSS var.
    useEffect(() => {
        const root = shellRef.current;
        if (!root) return;

        const selectors = [
            'header',
            '[role="banner"]',
            '.navbar',
            '.site-navbar',
            '.top-nav',
            '.app-navbar',
        ];

        const getNavEl = () => {
            for (const sel of selectors) {
                const el = document.querySelector(sel);
                if (el) return el;
            }
            return null;
        };

        let raf = 0;

        const apply = () => {
            raf = 0;
            const navEl = getNavEl();
            if (!navEl) return;

            const rect = navEl.getBoundingClientRect();
            // When the navbar hides via translateY/scroll, rect.bottom shrinks toward 0.
            const visibleH = Math.max(0, Math.min(window.innerHeight, rect.bottom));
            root.style.setProperty('--cd-nav-h', `${Math.round(visibleH)}px`);
        };

        const schedule = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, []);
    // --------------------
    // UI
    // --------------------
    return (
        <>
            <Navbar />
            <div className="cd-shell" ref={shellRef}>
                <div className="nd-content">
                    <aside className="nd-sidebar">
                        <div className="nd-sidebarHeader">
                            <div className="nd-sidebarTitle">{schoolName}</div>
                            <div className="nd-sidebarSub">Chapter</div>
                        </div>
                        <nav className="nd-nav">
                            <button
                                type="button"
                                className={`nd-navItem ${tab === 'home' ? 'is-active' : ''}`}
                                onClick={() => setTab('home')}
                            >
                                Home
                            </button>
                            <button
                                type="button"
                                className={`nd-navItem ${tab === 'talks' ? 'is-active' : ''}`}
                                onClick={() => setTab('talks')}
                            >
                                Talks
                            </button>
                            <button
                                type="button"
                                className={`nd-navItem ${tab === 'users' ? 'is-active' : ''}`}
                                onClick={() => setTab('users')}
                            >
                                Users
                            </button>
                        </nav>
                        <div className="nd-sidebarFooter">
                            <div className="nd-muted" style={{ fontSize: 12 }}>
                                {currentUser?.email ? (
                                    <>
                                        Signed in as <strong>{currentUser.email}</strong>
                                    </>
                                ) : (
                                    <>Not signed in</>
                                )}
                                <div style={{ marginTop: 6 }}>
                                    Role: <strong>{(isCorporateUser || isNationalEditorUser) ? String(globalUserRole || 'member') : normalizeRole(myRole)}</strong>
                                    {(isCorporateUser || isNationalEditorUser) ? (
                                        <span style={{ marginLeft: 8, fontWeight: 800 }}>
                                            ({isCorporateUser ? 'Corporate' : 'National'})
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </aside>
                    <main className="nd-page">
                        <div className="nd-header">
                            <div>
                                <div className="nd-title">
                                    {tab === 'home' ? 'Home' : tab === 'talks' ? 'Talks' : 'Users'}
                                </div>
                                <div className="nd-subtitle">
                                    {tab === 'home'
                                        ? 'Overview, quick links, and visibility.'
                                        : tab === 'talks'
                                            ? 'Events grouped by semester, with a public window.'
                                            : 'Members and roles for this chapter.'}
                                </div>
                            </div>

                            <div className="nd-headerActions">
                                {publicPageUrl ? (
                                    <Link to={publicPageUrl} className="nd-linkBtn" target="_blank" rel="noopener noreferrer">
                                        View Public Page
                                    </Link>
                                ) : null}
                            </div>
                        </div>

                        {shellNote ? (
                            <div className="nd-panel" style={{ marginBottom: 14 }}>
                                <div className="nd-muted">{shellNote}</div>
                                <div className="nd-muted" style={{ marginTop: 6, fontSize: 12 }}>
                                    Tip: each school needs a unique <strong>slug</strong> field in Firestore, and this page uses <code>/manage/[slug]</code>.
                                </div>
                            </div>
                        ) : null}

                        {/* HOME */}
                        {tab === 'home' ? (
                            <>
                                <div className="nd-grid" style={{ marginBottom: 14 }}>
                                    <div className="nd-panel">
                                        <div className="nd-panelHeader">
                                            <div>
                                                <div className="nd-panelTitle">Chapter</div>
                                                <div className="nd-muted">Basic info and quick links.</div>
                                            </div>
                                        </div>

                                        <div className="nd-panelBody">
                                            <div className="nd-row" style={{ marginBottom: 10, alignItems: 'center' }}>
                                                <div className="nd-label">Campus</div>
                                                <div className="nd-value" style={{ width: '100%' }}>
                                                    <input
                                                        className="nd-input"
                                                        value={schoolDisplayName}
                                                        onChange={(e) => setSchoolDisplayName(e.target.value)}
                                                        placeholder="University name"
                                                        disabled={!canEditHome}
                                                    />
                                                </div>
                                            </div>

                                            <div className="nd-row" style={{ marginBottom: 10 }}>
                                                <div className="nd-label">Slug</div>
                                                <div className="nd-value"><code>{schoolDoc?.slug || routeKey || ''}</code></div>
                                            </div>

                                            <div className="nd-row" style={{ marginBottom: 14, alignItems: 'center' }}>
                                                <div className="nd-label">Active</div>
                                                <div className="nd-value" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!active}
                                                            onChange={(e) => setActive(!!e.target.checked)}
                                                            disabled={!canEditHome}
                                                        />
                                                        <span className="nd-muted">Show this chapter publicly</span>
                                                    </label>
                                                    {homeStatus ? <span className="nd-muted">{homeStatus}</span> : null}
                                                </div>
                                            </div>

                                            <div className="nd-row" style={{ alignItems: 'flex-start' }}>
                                                <div className="nd-label">Links</div>
                                                <div className="nd-value" style={{ width: '100%' }}>
                                                    <div className="nd-formGrid">
                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                            <div className="nd-fieldLabel">Group chat link</div>
                                                            <input
                                                                className="nd-input"
                                                                value={ctaGroupMe}
                                                                onChange={(e) => setCtaGroupMe(e.target.value)}
                                                                placeholder="https://discord.gg/... or https://groupme.com/join/..."
                                                                disabled={!canEditHome}
                                                            />
                                                        </label>

                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                            <div className="nd-fieldLabel">Email list link</div>
                                                            <input
                                                                className="nd-input"
                                                                value={ctaEmail}
                                                                onChange={(e) => setCtaEmail(e.target.value)}
                                                                placeholder="https://soleministries.org/subscribe"
                                                                disabled={!canEditHome}
                                                            />
                                                        </label>

                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                            <div className="nd-fieldLabel">Instagram URL</div>
                                                            <input
                                                                className="nd-input"
                                                                value={ctaInstagram}
                                                                onChange={(e) => setCtaInstagram(e.target.value)}
                                                                placeholder="https://www.instagram.com/sole_ministries/"
                                                                disabled={!canEditHome}
                                                            />
                                                        </label>

                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                            <div className="nd-fieldLabel">Chapter website</div>
                                                            <input
                                                                className="nd-input"
                                                                value={chapterWebsite}
                                                                onChange={(e) => setChapterWebsite(e.target.value)}
                                                                placeholder="https://..."
                                                                disabled={!canEditHome}
                                                            />
                                                        </label>
                                                    </div>


                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="nd-panel">
                                        <div className="nd-panelHeader">
                                            <div>
                                                <div className="nd-panelTitle">Quick Look</div>
                                            </div>
                                        </div>
                                        <div className="nd-panelBody">
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                                                <div className="nd-card" style={{ padding: 14 }}>
                                                    <div className="nd-muted" style={{ fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                                                        Next talk
                                                    </div>
                                                    {nextTalk && nextTalkCountdown ? (
                                                        <>
                                                            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
                                                                {nextTalkCountdown.days}d {nextTalkCountdown.hours}h {nextTalkCountdown.mins}m
                                                            </div>
                                                            <div className="nd-muted" style={{ marginTop: 6 }}>
                                                                {nextTalk.title || 'Upcoming talk'}
                                                                {nextTalk.date ? ` • ${nextTalk.date}` : ''}
                                                                {nextTalk.time ? ` • ${nextTalk.time}` : ''}
                                                                {nextTalk._semName ? ` • ${nextTalk._semName}` : ''}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="nd-muted" style={{ marginTop: 6 }}>
                                                            No upcoming talks found.
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="nd-card" style={{ padding: 14 }}>
                                                    <div className="nd-muted" style={{ fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                                                        Total talks this semester
                                                    </div>
                                                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
                                                        {totalTalksThisSemester}
                                                    </div>
                                                    <div className="nd-muted" style={{ marginTop: 6 }}>
                                                        {currentSemesterForWidgets?.name || '—'}
                                                    </div>
                                                </div>

                                                <div className="nd-card" style={{ padding: 14 }}>
                                                    <div className="nd-muted" style={{ fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                                                        Avg attendance (counted talks)
                                                    </div>
                                                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
                                                        {avgAttendanceThisSemester === null
                                                            ? '—'
                                                            : Number.isInteger(avgAttendanceThisSemester)
                                                                ? avgAttendanceThisSemester
                                                                : avgAttendanceThisSemester.toFixed(1)}
                                                    </div>
                                                    <div className="nd-muted" style={{ marginTop: 6 }}>
                                                        {currentSemesterForWidgets?.name || '—'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="nd-panel" style={{ marginBottom: 14 }}>
                                    <div className="nd-panelHeader">
                                        <div>
                                            <div className="nd-panelTitle">Announcements</div>
                                            <div className="nd-muted">Shown on this chapter’s public page (when active).</div>
                                        </div>
                                    </div>
                                    <div className="nd-panelBody">
                                        <textarea
                                            className="nd-textarea"
                                            value={chapterAnnouncements}
                                            onChange={(e) => setChapterAnnouncements(e.target.value)}
                                            placeholder="Share chapter updates (room changes, sign-up links, reminders)…"
                                            disabled={!canEditHome}
                                            rows={6}
                                        />
                                    </div>
                                </div>

                                <div className="nd-panel">
                                    <div className="nd-panelHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div className="nd-panelTitle">Notes</div>
                                            <div className="nd-muted">Internal notes for leaders (not public).</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            {canEditHome ? (
                                                <button type="button" className="nd-btn" onClick={() => setShowAddNoteModal(true)}>
                                                    + Add Note
                                                </button>
                                            ) : null}
                                            <button type="button" className="nd-btn" onClick={refreshInternalNotes} disabled={notesLoading}>
                                                {notesLoading ? 'Refreshing…' : 'Refresh'}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="nd-panelBody">
                                        {notesStatus ? <div className="nd-muted" style={{ marginBottom: 10 }}>{notesStatus}</div> : null}

                                        {(() => {
                                            const visible = (Array.isArray(internalNotes) ? internalNotes : []).filter((n) => !n.dismissed);
                                            if (!visible.length) {
                                                return <div className="nd-muted">No notes yet. Add a note for reminders, follow-ups, or operational context.</div>;
                                            }
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    {visible.map((n) => (
                                                        <div key={n.id} className="nd-card" style={{ padding: 12, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontWeight: 800 }}>{n.authorName || 'Unknown'}</div>
                                                                <div className="nd-muted" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{n.text}</div>
                                                                {n.createdAt ? (
                                                                    <div className="nd-muted" style={{ marginTop: 6, fontSize: 12 }}>
                                                                        {formatIsoForDisplay(n.createdAt)}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                                                                {canEditHome ? (
                                                                    <button
                                                                        type="button"
                                                                        className="nd-btn"
                                                                        title="Dismiss"
                                                                        onClick={() => dismissInternalNote(n.id)}
                                                                        style={{ padding: '6px 10px' }}
                                                                    >
                                                                        ✓
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}

                                        {!canEditHome ? (
                                            <div className="nd-muted" style={{ marginTop: 10, fontSize: 12 }}>
                                                View-only access.
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                {showAddNoteModal ? (
                                    <div className="nd-modalOverlay" onMouseDown={() => setShowAddNoteModal(false)}>
                                        <div className="nd-modal" onMouseDown={(e) => e.stopPropagation()}>
                                            <div className="nd-modalHeader">
                                                <div className="nd-modalTitle">Add Note</div>
                                                <button type="button" className="nd-btn" onClick={() => setShowAddNoteModal(false)}>Close</button>
                                            </div>
                                            <div className="nd-modalBody">
                                                <label className="nd-field" style={{ width: '100%' }}>
                                                    <div className="nd-fieldLabel">Note</div>
                                                    <textarea
                                                        className="nd-textarea"
                                                        value={newNoteText}
                                                        onChange={(e) => setNewNoteText(e.target.value)}
                                                        rows={5}
                                                        placeholder="Write a note…"
                                                    />
                                                </label>
                                            </div>
                                            <div className="nd-modalFooter" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                                <button type="button" className="nd-btn" onClick={() => setShowAddNoteModal(false)}>Cancel</button>
                                                <button type="button" className="nd-btnPrimary" onClick={addInternalNote}>Save Note</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </>
                        ) : null}

                        {/* TALKS */}
                        {tab === 'talks' ? (
                            <>
                                <div className="nd-panel" style={{ marginBottom: 14 }}>
                                    <div className="nd-panelHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div className="nd-panelTitle">Semesters</div>
                                            <div className="nd-muted">Select a semester to manage events and its public window.</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <button type="button" className="nd-btn" onClick={addSemester} disabled={!canEditTalks}>
                                                + Add semester
                                            </button>
                                            <button
                                                type="button"
                                                className="nd-btnPrimary"
                                                onClick={saveTalksData}
                                                disabled={!canEditTalks || savingTalks || !schoolId}
                                            >
                                                {savingTalks ? 'Saving…' : 'Save'}
                                            </button>
                                            {talksStatus ? <span className="nd-muted">{talksStatus}</span> : null}
                                        </div>
                                    </div>

                                    <div className="nd-panelBody">
                                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <label className="nd-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                Semester
                                                <select
                                                    value={selectedSemesterId}
                                                    onChange={(e) => setSelectedSemesterId(e.target.value)}
                                                    className="nd-select"
                                                    style={{ minWidth: 220 }}
                                                >
                                                    {(semesters || []).map((s) => (
                                                        <option key={s.id} value={s.id}>
                                                            {s.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {selectedSemester ? (
                                                    <label className="nd-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                        Name
                                                        <input
                                                            className="nd-input"
                                                            value={selectedSemester.name || ''}
                                                            onChange={(e) => updateSemesterField(selectedSemester.id, 'name', e.target.value)}
                                                            disabled={!canEditTalks}
                                                            style={{ minWidth: 220 }}
                                                            placeholder="Semester name"
                                                        />
                                                    </label>
                                                ) : null}
                                            </label>

                                            {selectedSemester ? (
                                                <>
                                                    <label className="nd-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                        Public from
                                                        <input
                                                            type="date"
                                                            value={selectedSemester.publicFrom || ''}
                                                            onChange={(e) => updateSemesterField(selectedSemester.id, 'publicFrom', e.target.value)}
                                                            className="nd-input"
                                                            disabled={!canEditTalks}
                                                        />
                                                    </label>
                                                    <label className="nd-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                        Public to
                                                        <input
                                                            type="date"
                                                            value={selectedSemester.publicTo || ''}
                                                            onChange={(e) => updateSemesterField(selectedSemester.id, 'publicTo', e.target.value)}
                                                            className="nd-input"
                                                            disabled={!canEditTalks}
                                                        />
                                                    </label>
                                                    {canEditTalks ? (
                                                        <button
                                                            type="button"
                                                            className="nd-linkBtn"
                                                            onClick={() => removeSemester(selectedSemester.id)}
                                                            title="Remove semester"
                                                        >
                                                            Remove
                                                        </button>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </div>

                                        {!canEditTalks ? (
                                            <div className="nd-muted" style={{ marginTop: 10 }}>
                                                You have view-only access. Ask a leader/president to edit talks.
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                {selectedSemester ? (
                                    <>
                                        <div className="nd-panel">
                                            <div className="nd-panelHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div className="nd-panelTitle">Events</div>
                                                    <div className="nd-muted">Add talks for {selectedSemester.name}.</div>
                                                </div>
                                                {canEditTalks ? (
                                                    <button type="button" className="nd-btn" onClick={() => addEventToSemester(selectedSemester.id)}>
                                                        + Add event
                                                    </button>
                                                ) : null}
                                            </div>

                                            <div className="nd-panelBody">
                                                {(selectedSemester.events || []).length ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                        {(selectedSemester.events || []).map((evt, idx) => (
                                                            <div key={`${selectedSemester.id}_${idx}`} className="nd-card">
                                                                <div className="nd-panelHeader">
                                                                    <div className="nd-panelTitle">{evt.title || `Event ${idx + 1}`}</div>
                                                                    {canEditTalks ? (
                                                                        <button
                                                                            type="button"
                                                                            className="nd-linkBtn"
                                                                            onClick={() => removeEventFromSemester(selectedSemester.id, idx)}
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    ) : null}
                                                                </div>

                                                                <div className="nd-panelBody">
                                                                    <div className="nd-formGrid">
                                                                        <label className="nd-field">
                                                                            <div className="nd-fieldLabel">Title</div>
                                                                            <input
                                                                                className="nd-input"
                                                                                value={evt.title || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'title', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                placeholder="Talk title"
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field">
                                                                            <div className="nd-fieldLabel">Date</div>
                                                                            <input
                                                                                type="date"
                                                                                className="nd-input"
                                                                                value={evt.date || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'date', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field">
                                                                            <div className="nd-fieldLabel">Time</div>
                                                                            <input
                                                                                type="time"
                                                                                className="nd-input"
                                                                                value={evt.time || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'time', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field">
                                                                            <div className="nd-fieldLabel">Location</div>
                                                                            <input
                                                                                className="nd-input"
                                                                                value={evt.location || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'location', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                placeholder="Building / room"
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                                            <div className="nd-fieldLabel">Description</div>
                                                                            <textarea
                                                                                className="nd-textarea"
                                                                                value={evt.description || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'description', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                rows={3}
                                                                                placeholder="Short description for the website…"
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                                            <div className="nd-fieldLabel">Talk page link</div>
                                                                            <input
                                                                                className="nd-input"
                                                                                value={evt.talkLink || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'talkLink', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}
                                                                                placeholder="https://soleministries.org/talk/..."
                                                                            />
                                                                        </label>
                                                                        <label className="nd-field" style={{ gridColumn: '1 / -1' }}>
                                                                            <div className="nd-fieldLabel">Talk recording link</div>
                                                                            <input
                                                                                className="nd-input"
                                                                                value={evt.talkRecordingLink || ''}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, 'talkRecordingLink', e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}
                                                                                placeholder="https://..."
                                                                            />
                                                                        </label>
                                                                        <label className="nd-field">
                                                                            <div className="nd-fieldLabel">Attendance</div>
                                                                            <input
                                                                                type="number"
                                                                                className="nd-input"
                                                                                value={evt.attendanceCount ?? ""}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, "attendanceCount", e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                placeholder="0"
                                                                                min={0}
                                                                            />
                                                                        </label>

                                                                        <label className="nd-field" style={{ gridColumn: "1 / -1" }}>
                                                                            <div className="nd-fieldLabel">Attendance notes</div>
                                                                            <textarea
                                                                                className="nd-textarea"
                                                                                value={evt.attendanceNotes ?? ""}
                                                                                onChange={(e) => updateEvent(selectedSemester.id, idx, "attendanceNotes", e.target.value)}
                                                                                disabled={!canEditTalks}
                                                                                rows={2}
                                                                                placeholder="Optional notes (how counted, special events, etc.)"
                                                                            />
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="nd-muted">No events yet. {canEditTalks ? 'Click “Add event”.' : ''}</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="nd-panel">
                                        <div className="nd-panelBody">
                                            <div className="nd-muted">No semester selected.</div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : null}

                        {/* USERS */}
                        {tab === 'users' ? (
                            <>
                                <div className="nd-panel" style={{ marginBottom: 14 }}>
                                    <div className="nd-panelHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div className="nd-panelTitle">Members</div>
                                            <div className="nd-muted">
                                                Presidents can add/remove people and change roles. Leaders can add people. Members are view-only.
                                            </div>
                                        </div>

                                        <div className="nd-muted" style={{ fontSize: 12 }}>
                                            {loadingMembers ? 'Loading…' : `${members.length} member${members.length === 1 ? '' : 's'}`}
                                        </div>
                                    </div>

                                    <div className="nd-panelBody">
                                        {canAddMembers(myRole, isCorporateUser) ? (
                                            <div className="nd-card" style={{ marginBottom: 14 }}>
                                                <div className="nd-panelHeader">
                                                    <div className="nd-panelTitle">Add member</div>
                                                </div>
                                                <div className="nd-panelBody">
                                                    <div className="nd-formGrid">
                                                        <label className="nd-field">
                                                            <div className="nd-fieldLabel">Email</div>
                                                            <input
                                                                className="nd-input"
                                                                value={addEmail}
                                                                onChange={(e) => setAddEmail(e.target.value)}
                                                                placeholder="name@school.edu"
                                                            />
                                                        </label>

                                                        <label className="nd-field">
                                                            <div className="nd-fieldLabel">Name (optional)</div>
                                                            <input
                                                                className="nd-input"
                                                                value={addName}
                                                                onChange={(e) => setAddName(e.target.value)}
                                                                placeholder="Full name"
                                                            />
                                                        </label>

                                                        <label className="nd-field">
                                                            <div className="nd-fieldLabel">Role</div>
                                                            <select
                                                                className="nd-select"
                                                                value={addRole}
                                                                onChange={(e) => setAddRole(e.target.value)}
                                                                disabled={!canManageMembers(myRole, isCorporateUser)}
                                                                title={!canManageMembers(myRole, isCorporateUser) ? 'Only presidents can assign leader/president roles.' : ''}
                                                            >
                                                                <option value="member">member</option>
                                                                <option value="leader">leader</option>
                                                                <option value="president">president</option>
                                                            </select>
                                                        </label>

                                                        <div className="nd-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                                            <button type="button" className="nd-btnPrimary" onClick={addMember}>
                                                                Add
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {memberStatus ? <div className="nd-muted" style={{ marginTop: 10 }}>{memberStatus}</div> : null}
                                                    {!canManageMembers(myRole, isCorporateUser) ? (
                                                        <div className="nd-muted" style={{ marginTop: 10, fontSize: 12 }}>
                                                            Leaders can add members, but only presidents can promote/demote or remove people.
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="nd-muted" style={{ marginBottom: 14 }}>
                                                You have view-only access.
                                            </div>
                                        )}

                                        <div className="nd-tableWrap">
                                            <table className="nd-table">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        {canManageMembers(myRole, isCorporateUser) ? <th style={{ width: 160 }}>Actions</th> : null}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {members.length ? (
                                                        members.map((m) => (
                                                            <tr key={m.id}>
                                                                <td>{m.name || '—'}</td>
                                                                <td>{m.email || '—'}</td>
                                                                <td>
                                                                    {canManageMembers(myRole, isCorporateUser) ? (
                                                                        <select
                                                                            className="nd-select"
                                                                            value={normalizeRole(m.role)}
                                                                            onChange={(e) => updateMemberRole(m.id, e.target.value)}
                                                                        >
                                                                            <option value="member">member</option>
                                                                            <option value="leader">leader</option>
                                                                            <option value="president">president</option>
                                                                        </select>
                                                                    ) : (
                                                                        <span>{normalizeRole(m.role)}</span>
                                                                    )}
                                                                </td>
                                                                {canManageMembers(myRole, isCorporateUser) ? (
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="nd-linkBtn"
                                                                            onClick={() => removeMember(m)}
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    </td>
                                                                ) : null}
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={canManageMembers(myRole, isCorporateUser) ? 4 : 3} className="nd-muted">
                                                                {loadingMembers ? 'Loading…' : 'No members found.'}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {memberStatus && !canAddMembers(myRole, isCorporateUser) ? (
                                            <div className="nd-muted" style={{ marginTop: 10 }}>{memberStatus}</div>
                                        ) : null}
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {/* Loading states */}
                        {loadingSchool || loadingSchools ? (
                            <div className="nd-muted" style={{ marginTop: 12 }}>
                                Loading…
                            </div>
                        ) : null}
                        {toast.open && (
                            <div className="toast-container">
                                <div className={`toast toast-${toast.kind}`}>
                                    {toast.message}
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}